import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { DateRangeCalendar } from '@mui/x-date-pickers-pro/DateRangeCalendar';
import moment from 'moment';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import PickersLocalizationProvider from '../../components/BasicUI/PickersLocalizationProvider/PickersLocalizationProvider';
import Modal from '../../components/ModalNew/Modal';
import { END_DATE, START_DATE } from '../../util/dates';
import { firstBookedAfter, shouldDisableDateFn } from './BookingDatesForm.new.helpers';

/**
 * @param {[moment.Moment | null, moment.Moment | null]} bookingDates
 * @param {string | null} bookingLimit
 * @param {boolean} fetchTimeSlotsInProgress
 * @param {number} minimumNights
 * @param {(newValue: [moment.Moment | null, moment.Moment | null]) => void} onChange
 * @param {(monthOffset: number) => void} onMonthChange
 * @param {boolean} open
 * @param {() => void} onClose
 * @param {import('@vansite/ts-sharetribe-flex-sdk').TimeSlot[]} timeSlots
 */
const ChangeDatesModalComponent = ({
  bookingDates,
  bookingLimit,
  fetchTimeSlotsInProgress,
  minimumNights = 1,
  onChange,
  onMonthChange,
  open,
  onClose,
  timeSlots,
}) => {
  const intl = useIntl();
  const title = intl.formatMessage({ id: 'BookingDatesForm.changeDatesModalTitle' });

  // Internal state for dates being selected in the modal
  const [internalValue, setInternalValue] = useState([null, null]);
  const [focusedInput, setFocusedInput] = useState(START_DATE);

  // Initialize internal state with current booking dates when modal opens
  useEffect(() => {
    if (open) {
      setInternalValue(bookingDates || [null, null]);
      setFocusedInput(START_DATE);
    }
  }, [open, bookingDates]);

  // Calculate max date based on internal start date selection
  const effectiveMaxDate = useMemo(() => {
    const startDate = internalValue[0];
    if (!startDate) return null;

    // Calculate booking limit max
    let limitMax = moment(startDate).add(bookingLimit, 'days');

    // Find first blocked date after start
    const firstBlocked = firstBookedAfter(timeSlots, startDate);
    const blockedMax = firstBlocked || null;

    const candidates = [];
    if (limitMax) candidates.push(limitMax);
    if (blockedMax) candidates.push(blockedMax);

    return candidates.length > 0 ? moment.min(...candidates) : null;
  }, [internalValue[0], bookingLimit, timeSlots]);

  // Calculate shouldDisableDate based on internal state
  // MUI DateRangePicker passes (day, position) where position is "start" or "end"
  const shouldDisableDate = useMemo(
    () => shouldDisableDateFn(timeSlots, internalValue[0], bookingLimit, minimumNights),
    [timeSlots, internalValue[0], bookingLimit, minimumNights],
  );

  // Handle date changes
  const handleChange = useCallback((newValue) => {
    const [start, end] = newValue;

    // Reject end dates that violate the minimum nights requirement
    if (start && end && moment(end).diff(moment(start), 'days') < minimumNights) {
      setInternalValue([start, null]);
      setFocusedInput(END_DATE);
      return;
    }

    // Prevent selecting the same date for start and end (nightly bookings require at least 1 night)
    if (start && end && moment(start).isSame(moment(end), 'day')) {
      // Reset end date and keep focus on end date
      setInternalValue([start, null]);
      setFocusedInput(END_DATE);
      return;
    }

    setInternalValue(newValue);

    // Determine which input to focus next
    if (start && !end) {
      setFocusedInput(END_DATE);
    } else if (!start) {
      setFocusedInput(START_DATE);
    }

    // If both dates are selected, save and close
    if (start && end) {
      onChange(newValue);
      onClose();
    }
  }, [onChange, onClose, minimumNights]);

  // Determine maxDate for calendar navigation
  // Only apply restrictive maxDate when selecting end date
  const calendarMaxDate = useMemo(() => {
    if (focusedInput === END_DATE && effectiveMaxDate) {
      return effectiveMaxDate;
    }
    // Allow navigation up to 1 year for start date selection
    return moment().add(1, 'year').endOf('day');
  }, [focusedInput, effectiveMaxDate]);

  return (
    <Modal
      onClose={onClose}
      open={open}
      title={title}
    >
      <Box
        sx={{
          '& .MuiDayCalendar-loadingContainer': {
            minWidth: '312px',
            minHeight: '264px',
          },
        }}
      >
        <PickersLocalizationProvider>
          <DateRangeCalendar
            calendars={2}
            disablePast
            loading={fetchTimeSlotsInProgress}
            maxDate={calendarMaxDate}
            onChange={handleChange}
            onMonthChange={onMonthChange}
            referenceDate={(bookingDates?.[0] ? moment(bookingDates[0]) : moment()).startOf('day')}
            renderLoading={() => <CircularProgress size={24} />}
            shouldDisableDate={shouldDisableDate}
            value={internalValue}
          />
        </PickersLocalizationProvider>
      </Box>
    </Modal>
  );
};

const ChangeDatesModal = memo(ChangeDatesModalComponent);

export default ChangeDatesModal;
